import { useEffect, useSyncExternalStore } from 'react';
import { SKELETON_PREVIEW_PANEL_STORAGE_KEY, SKELETON_PREVIEW_STORAGE_KEY } from 'ud-ui/components/skeleton/skeleton-tokens';

/**
 * Режим просмотра скелетона при локальной разработке:
 * - `off` — экран как есть;
 * - `skeleton` — вместо загруженного экрана его скелетон;
 * - `overlay` — скелетон полупрозрачно поверх загруженного экрана: разъехавшиеся блоки видны
 *   сразу, без замера `getBoundingClientRect()` и без задержки ответа.
 */
export type SkeletonPreviewMode = 'off' | 'skeleton' | 'overlay';

export const SKELETON_PREVIEW_MODES: readonly SkeletonPreviewMode[] = ['off', 'skeleton', 'overlay'];

const isMode = (value: unknown): value is SkeletonPreviewMode => SKELETON_PREVIEW_MODES.includes(value as SkeletonPreviewMode);

function read(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string | null) {
  try {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch {
    // Хранилище недоступно — состояние живёт до перезагрузки.
  }
}

const readMode = (): SkeletonPreviewMode => {
  const stored = read(SKELETON_PREVIEW_STORAGE_KEY);
  return isMode(stored) ? stored : 'off';
};
/** Панель по умолчанию открыта; хранится только факт «скрыта». */
const readPanelOpen = () => read(SKELETON_PREVIEW_PANEL_STORAGE_KEY) !== 'hidden';

let mode: SkeletonPreviewMode = readMode();
let isPanelOpen = readPanelOpen();
/** Сколько `SkeletonPreview` сейчас на экране: панель показывается, только если есть что переключать. */
let mountedCount = 0;
const listeners = new Set<() => void>();

const emit = () => listeners.forEach((listener) => listener());

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export function setSkeletonPreviewMode(next: SkeletonPreviewMode) {
  mode = next;
  write(SKELETON_PREVIEW_STORAGE_KEY, next === 'off' ? null : next);
  emit();
}

export function setSkeletonPreviewPanelOpen(next: boolean) {
  isPanelOpen = next;
  write(SKELETON_PREVIEW_PANEL_STORAGE_KEY, next ? null : 'hidden');
  emit();
}

export const useSkeletonPreviewMode = () => useSyncExternalStore(subscribe, () => mode);

export const useSkeletonPreviewPanelOpen = () => useSyncExternalStore(subscribe, () => isPanelOpen);

export const useSkeletonPreviewMountedCount = () => useSyncExternalStore(subscribe, () => mountedCount);

/** Отмечает обёртку на экране, пока она смонтирована. */
export function useRegisterSkeletonPreview(isEnabled: boolean) {
  useEffect(() => {
    if (!isEnabled) return undefined;
    mountedCount += 1;
    emit();
    return () => {
      mountedCount -= 1;
      emit();
    };
  }, [isEnabled]);
}

/** Только для тестов: модуль хранит состояние между тестами одного файла. */
export function resetSkeletonPreviewStore() {
  mode = readMode();
  isPanelOpen = readPanelOpen();
  mountedCount = 0;
  emit();
}
